import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ScrollToTop from '@/components/feature/ScrollToTop';
import Breadcrumb from '@/components/feature/Breadcrumb';
import ArticleCard from '@/components/base/ArticleCard';
import { Helmet } from '@/components/base/Helmet';
import { getArticleBySlug, articles } from '@/mocks/articles';

export default function ArticlePage() {
  const { slug } = useParams<{ slug: string }>();
  const article = getArticleBySlug(slug || '');

  if (!article) {
    return (
      <>
        <Navbar />
        <ScrollToTop />
        <main className="pt-20 pb-16">
          <div className="max-w-7xl mx-auto px-4 md:px-6 text-center py-20">
            <h1 className="font-heading text-2xl font-semibold text-foreground-100 mb-3">文章未找到</h1>
            <Link to="/" className="text-primary-500 hover:text-primary-600 transition-colors">返回首页</Link>
          </div>
        </main>
        <Footer />
      </>
    );
  }

  const relatedArticles = articles
    .filter((a) => a.categorySlug === article.categorySlug && a.id !== article.id)
    .slice(0, 3);

  return (
    <>
      <Helmet
        title={`${article.title} - 亚洲自拍完全指南`}
        description={article.excerpt}
        keywords={article.tags.join(',')}
        canonical={`https://ahpinxing.cn/article/${article.slug}`}
        ogType="article"
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Article",
            "headline": article.title,
            "description": article.excerpt,
            "author": {
              "@type": "Person",
              "name": article.author,
            },
            "datePublished": article.date,
            "image": article.image,
            "publisher": {
              "@type": "Organization",
              "name": "亚洲自拍完全指南",
            },
          }),
        }}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "BreadcrumbList",
            "itemListElement": [
              {
                "@type": "ListItem",
                "position": 1,
                "name": "首页",
                "item": "https://ahpinxing.cn",
              },
              {
                "@type": "ListItem",
                "position": 2,
                "name": article.category,
                "item": `https://ahpinxing.cn/category/${article.categorySlug}`,
              },
              {
                "@type": "ListItem",
                "position": 3,
                "name": article.title,
              },
            ],
          }),
        }}
      />
      <Navbar />
      <ScrollToTop />
      <main>
        <article className="pt-20 pb-8 bg-background-50">
          <div className="max-w-4xl mx-auto px-4 md:px-6">
            <Breadcrumb
              items={[
                { label: '首页', path: '/' },
                { label: article.category, path: `/category/${article.categorySlug}` },
                { label: article.title },
              ]}
            />

            <div className="mt-6">
              <span className="inline-block text-xs font-medium text-primary-500 bg-primary-50 px-2.5 py-1 rounded-md mb-4">
                {article.category}
              </span>
              <h1 className="font-heading text-2xl md:text-4xl font-semibold text-foreground-100 mb-4 leading-tight">
                {article.title}
              </h1>
              <div className="flex flex-wrap items-center gap-4 text-sm text-foreground-400 mb-6">
                <span className="flex items-center gap-1">
                  <i className="ri-user-line"></i>
                  {article.author}
                </span>
                <span className="flex items-center gap-1">
                  <i className="ri-calendar-line"></i>
                  {article.date}
                </span>
                <span className="flex items-center gap-1">
                  <i className="ri-time-line"></i>
                  阅读 {article.readTime}
                </span>
              </div>

              <div className="aspect-[2/1] rounded-lg overflow-hidden mb-8">
                <img
                  src={article.image}
                  alt={article.title}
                  className="w-full h-full object-cover"
                />
              </div>
            </div>
          </div>
        </article>

        <div className="bg-background-100 py-8 md:py-12">
          <div className="max-w-3xl mx-auto px-4 md:px-6">
            <div className="prose-custom space-y-6">
              {article.content.map((section, index) => {
                if (section.type === 'heading') {
                  return (
                    <h2 key={index} className="font-heading text-xl md:text-2xl font-semibold text-foreground-100 mt-10 mb-4">
                      {section.content}
                    </h2>
                  );
                }
                if (section.type === 'paragraph') {
                  return (
                    <p key={index} className="text-foreground-300 leading-relaxed text-sm md:text-base">
                      {section.content}
                    </p>
                  );
                }
                if (section.type === 'image' && section.imageUrl) {
                  return (
                    <figure key={index} className="my-6">
                      <div className="aspect-[16/9] rounded-lg overflow-hidden">
                        <img
                          src={section.imageUrl}
                          alt={section.imageAlt || ''}
                          className="w-full h-full object-cover"
                          loading="lazy"
                        />
                      </div>
                      {section.imageAlt && (
                        <figcaption className="text-xs text-foreground-500 text-center mt-2">
                          {section.imageAlt}
                        </figcaption>
                      )}
                    </figure>
                  );
                }
                if (section.type === 'tip') {
                  return (
                    <div key={index} className="flex gap-3 p-4 rounded-lg bg-accent-50 border border-accent-200/60 my-6">
                      <div className="w-6 h-6 flex items-center justify-center flex-shrink-0 mt-0.5">
                        <i className="ri-lightbulb-line text-accent-500 text-lg"></i>
                      </div>
                      <div>
                        <p className="text-sm text-foreground-200 leading-relaxed">{section.content}</p>
                      </div>
                    </div>
                  );
                }
                if (section.type === 'list' && section.items) {
                  return (
                    <div key={index} className="my-6">
                      <h3 className="font-heading text-base font-semibold text-foreground-100 mb-3">
                        {section.content}
                      </h3>
                      <ul className="space-y-2">
                        {section.items.map((item, i) => (
                          <li key={i} className="flex items-start gap-2 text-sm text-foreground-300">
                            <span className="w-5 h-5 flex items-center justify-center flex-shrink-0 mt-0.5">
                              <i className="ri-check-line text-accent-500 text-sm"></i>
                            </span>
                            {item}
                          </li>
                        ))}
                      </ul>
                    </div>
                  );
                }
                return null;
              })}
            </div>

            <div className="mt-10 pt-6 border-t border-background-200/70">
              <div className="flex flex-wrap gap-2">
                {article.tags.map((tag) => (
                  <span
                    key={tag}
                    className="inline-block px-3 py-1 text-xs bg-background-200 text-foreground-400 rounded-full"
                  >
                    #{tag}
                  </span>
                ))}
              </div>
            </div>
          </div>
        </div>

        {relatedArticles.length > 0 && (
          <section className="py-12 md:py-16 bg-background-50">
            <div className="max-w-7xl mx-auto px-4 md:px-6">
              <h2 className="font-heading text-xl md:text-2xl font-semibold text-foreground-100 mb-6">
                相关文章
              </h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-5">
                {relatedArticles.map((ra) => (
                  <ArticleCard key={ra.id} article={ra} />
                ))}
              </div>
            </div>
          </section>
        )}
      </main>
      <Footer />
    </>
  );
}